import {
    SharedTradeFiltersInterface,
    SharedTradeInterface,
    tradeSharingStore,
} from '../../../ng-services/social-trading/trade-sharing.store';
import { BaseExpandableTableComponent } from '../table-components/base-expandable-table.component';
import { LightboxService } from '../../../ng-services/lightbox.service';
import { getSharedTradeDirection, SharedTradeDirection } from '../../../services/get-shared-trade-direction';
import { getSharedTradeStrategy } from '../../../services/get-shared-trade-strategy';

declare function featureEnabled(feature: string): boolean;

export class TradeSharingTableComponent extends BaseExpandableTableComponent<
    SharedTradeInterface,
    SharedTradeFiltersInterface
> {
    static $inject = ['LightboxService', '$uibModal'];

    constructor(protected lightboxService: LightboxService, protected $uibModal: any) {
        super(tradeSharingStore.items);
    }

    getPostUrl(trade: SharedTradeInterface) {
        if (trade.posts.length === 0) {
            return '';
        }
        return `/post/${trade.posts[0].id}`;
    }

    getPost(trade: SharedTradeInterface) {
        return trade.posts?.length ? trade.posts?.[0] : null;
    }

    getChannelName(trade: SharedTradeInterface) {
        return this.getPost(trade)?.postable?.name ?? null;
    }

    getChannelUrl(trade: SharedTradeInterface) {
        const post = this.getPost(trade);

        if (!post) {
            return null;
        }

        switch (post.entry) {
            case 'group':
                return `/group/${post.entry_id}`;
            case 'webinar':
                return `/webinar/${post.entry_id}`;
            case 'user':
                return `/${post.entry_id}`;
        }

        return null;
    }

    getPostContent(trade: SharedTradeInterface) {
        return this.getPost(trade)?.content;
    }

    getPhoto(trade: SharedTradeInterface) {
        return this.getPost(trade)?.photos?.length ? this.getPost(trade)?.photos?.[0] : null;
    }

    // todo: missing types
    getTraderName(trade: any) {
        return trade?.user?.name;
    }

    getTraderUrl(trade: any) {
        const userId = trade?.user?.id;
        return `/${userId}`;
    }

    getSetupDownloadUrl(trade: SharedTradeInterface) {
        const post = this.getPost(trade);
        const document = post?.documents?.length && post?.documents?.[0];

        if (!(post && document)) {
            return false;
        }

        return `/post/${post.id}/document/${document.storage_id}/download`;
    }

    getStrategy(trade: SharedTradeInterface) {
        return getSharedTradeStrategy(trade) || 'ManualMode';
    }

    getStrategyTooltipText(trade: SharedTradeInterface) {
        const dir = getSharedTradeDirection(trade);
        switch (dir) {
            case SharedTradeDirection.SHORT:
                return 'Short Strategy';
            case SharedTradeDirection.LONG:
                return 'Long Strategy';
            default:
                return '';
        }
    }

    getStrategyClass(trade: SharedTradeInterface) {
        const dir = getSharedTradeDirection(trade);
        switch (dir) {
            case SharedTradeDirection.SHORT:
                return 'is-short';
            case SharedTradeDirection.LONG:
                return 'is-long';
            default:
                return '';
        }
    }

    openLightbox(trade: SharedTradeInterface) {
        const photo = this.getPhoto(trade);
        this.lightboxService.open({
            id: photo?.storage_id,
            preview_url: photo?.url,
            download_url: photo?.url,
            name: 'Setup Image',
            file_mimetype: photo?.type || 'image/png',
        });
    }
}

angular.module('tradersyard').component('tyTradeSharingTable', {
    // language=HTML
    template: `
        <div is-uib>
            <table class="trade_sharing_table table table-hover" ng-class="{'has-sticky-header': $ctrl.hasStickyHeader}" mobx-autorun>
                <thead>
                <tr>
                    <th class="is-sticky">Symbol</th>
                    <th class="is-sticky">Strategy</th>
                    <th class="is-sticky is-hidden-md">Channel</th>
                    <th class="is-sticky is-hidden-md">Trader</th>
                    <th class="is-sticky is-hidden-sm">Date</th>
                    <th class="is-sticky">Type</th>
                    <th class="is-sticky">Setup Image</th>
                    <th class="is-sticky">Trade</th>
                    <th class="is-sticky">Post</th>
                    <th class="is-sticky text-left col-chart is-hidden-xl">Chart</th>
                </tr>
                </thead>
                <tbody>
                <tr ng-if="$ctrl.isEmpty" class="trade_sharing_table__empty">
                    <td colspan="10">No trades found</td>
                </tr>
                <tr ng-repeat-start="row in $ctrl.data track by row.id"  mobx-autorun ng-click="$ctrl.toggle(row.id)">
                    <td class="col-symbol">
                        <ty-symbol-link symbol="row.symbol"/>
                    </td>
                    <td class="col-strategy" ng-class="$ctrl.getStrategyClass(row)" uib-tooltip="{{$ctrl.getStrategyTooltipText(row)}}">{{$ctrl.getStrategy(row)}}</td>
                    <td class="col-channel has-limited-width is-hidden-md">
                        <a ng-href="{{$ctrl.getChannelUrl(row)}}" uib-tooltip="{{$ctrl.getChannelName(row)}}">{{$ctrl.getChannelName(row)}}</a>
                    </td>
                    <td class="col-trader has-limited-width is-hidden-md">
                        <a ng-href="{{$ctrl.getTraderUrl(row)}}" ng-if="$ctrl.getTraderName(row)" uib-tooltip="{{$ctrl.getTraderName(row)}}">{{$ctrl.getTraderName(row)}}</a>
                    </td>
                    <td class="col-date is-hidden-sm">
                        {{row.created_at|moment:'L LT'}}
                    </td>
                    <td class="col-type">
                        {{row.type}}
                    </td>
                    <td class="col-setup-image">
                        <button class="btn btn-xs btn-default" ng-if="$ctrl.getPhoto(row)" ng-click="$ctrl.openLightbox(row); $event.stopPropagation();">Show</button>
                    </td>
                    <td class="col-trade">
                        <a
                            ng-href="{{$ctrl.getSetupDownloadUrl(row)}}"
                            ng-click="$event.stopPropagation()"
                            class="btn btn-xs btn-default"
                            ng-if="$ctrl.getSetupDownloadUrl(row)"
                        >Download</a>
                    </td>
                    <td class="col-post">
                        <a ng-href="{{$ctrl.getPostUrl(row)}}">View Post</a>
                    </td>
                    <td class="col-chart is-hidden-xl">
                        <ty-mini-day-chart symbol="row.symbol" mobx-autorun/>
                    </td>
                </tr>
                <tr class="details" ng-if="!$ctrl.isCollapsed(row.id)" ng-repeat-end>
                    <td colspan="10">
                        <ty-trade-sharing-table-row-details-component trade="row"/>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    `,
    controller: TradeSharingTableComponent,
    bindings: {
        hasStickyHeader: '<',
    },
});
